import { Dispatch, SetStateAction } from 'react';
import { ControlGroup, FormGroup } from '@blueprintjs/core';
import { ace_index } from '@axo/shared/data-access/types';
import { useAvailableClients } from '../../hooks/utils/useAvailableClients';
import { MultiSelect } from '../MultiSelect';
import {
  countries,
  currentCountries,
  currentRoles,
  currentUsers,
  roles,
  toggleCountry,
  toggleRole,
  toggleUser,
} from './EditCard';

interface Props {
  filter: ace_index.Audience;
  setFilter: Dispatch<SetStateAction<ace_index.Audience>>;
  disabled?: boolean;
}

export function ListFilter({ filter, setFilter, disabled = false }: Props) {
  const selectedCountries = currentCountries(asList(filter));
  const selectedRoles = currentRoles(asList(filter));
  const selectedUsers = currentUsers(asList(filter));

  const clients = useAvailableClients();

  return (
    <FormGroup inline label="Filter lists by" disabled={disabled}>
      <ControlGroup>
        <MultiSelect
          disabled={disabled}
          placeholder={disabled ? '' : 'Country'}
          data={countries}
          values={disabled ? [] : selectedCountries}
          stringify={(country) => country}
          onClear={() => setFilter((old) => ({ ...old, MarketCountries: [] }))}
          onSelect={(country) => setFilter(wrap(toggleCountry(country)))}
          onRemove={(_, i) =>
            setFilter(wrap(toggleCountry(selectedCountries[i])))
          }
        />

        <MultiSelect
          disabled={disabled}
          placeholder={disabled ? '' : 'Role'}
          data={roles}
          values={disabled ? [] : selectedRoles}
          stringify={(role) => role}
          onClear={() => setFilter((old) => ({ ...old, Roles: [] }))}
          onSelect={(country) => setFilter(wrap(toggleRole(country)))}
          onRemove={(_, i) => setFilter(wrap(toggleRole(selectedRoles[i])))}
        />

        <MultiSelect
          disabled={disabled}
          placeholder={disabled ? '' : 'User'}
          data={clients.map((client) => client.ID)}
          values={disabled ? [] : selectedUsers}
          stringify={(clientID) =>
            clients.find((client) => client.ID === clientID)?.DisplayName ?? '?'
          }
          keyify={(clientID) => clientID}
          onClear={() => setFilter((old) => ({ ...old, ClientIDs: [] }))}
          onSelect={(clientID) => setFilter(wrap(toggleUser(clientID)))}
          onRemove={(_, i) => setFilter(wrap(toggleUser(selectedUsers[i])))}
        />
      </ControlGroup>
    </FormGroup>
  );
}

// asList maps the audience to a complete working list.
// This is to make interop with existing functions more
// simple. It just creates a fake working list with the
// audience property set as defined.
function asList(aud: ace_index.Audience) {
  return { Audience: aud } as ace_index.WorkingList;
}

// wrap converts a state transformer for a complete
// working list to work on only the audience property.
// This is to interop with existing functions.
function wrap(
  transformer: SetStateAction<ace_index.WorkingList>,
): SetStateAction<ace_index.Audience> {
  return (prev) =>
    typeof transformer === 'function'
      ? transformer(asList(prev)).Audience
      : transformer.Audience;
}
